import {TopLevelMetricType} from 'common/types/reports/bookmark';
import {NullableString} from 'common/util/typescript';
import {
  isEventBasedEntry,
  isGroupedEntry,
  isMetricEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryEntry,
} from 'iron/common/query-builder/query-entry-section/types';

/**
 * Recursively handles nested behaviors for funnel event comparison.
 */
const getEventNameFromMetricEntryBehavior = (behavior: MetricEntryBehavior): NullableString | NullableString[] => {
  if (behavior.behaviors?.length) {
    return behavior.behaviors.flatMap(getEventNameFromMetricEntryBehavior);
  }
  return behavior.name;
};

const getEventNameFromEntry = (entry: QueryEntry | MetricEntry, names = new Set<string>()): string[] => {
  if (entry.type === TopLevelMetricType.NewFormulaEntry) {
    entry.referencedMetrics?.forEach((metric) => getEventNameFromEntry(metric, names));
  } else if (isMetricEntry(entry)) {
    const hasNestedBehaviors = Boolean(entry.behavior.behaviors?.length);
    const behaviorEventNames = hasNestedBehaviors
      ? entry.behavior.behaviors!.flatMap(getEventNameFromMetricEntryBehavior)
      : [entry.behavior.name];

    behaviorEventNames.forEach((name) => {
      if (name) {
        names.add(name);
      }
    });
  } else if (isGroupedEntry(entry)) {
    entry.event.forEach((event) => {
      if (event.event.value) {
        names.add(event.event.value);
      }
    });
  } else if (isEventBasedEntry(entry) && entry.event.value) {
    names.add(entry.event.value);
  }

  return Array.from(names);
};

/**
 * Extracts event names from a single behavior or an array of behaviors.

* @returns An array of event names extracted from the given behavior(s).
 * If a behavior has nested behaviors, those will be recursively processed.
 */
export function getEventNamesFromEntries(entries: QueryEntry | MetricEntry | Array<QueryEntry | MetricEntry>) {
  entries = Array.isArray(entries) ? entries : [entries];

  return entries.flatMap((entry) => getEventNameFromEntry(entry));
}
